import { useEffect, useRef, useState } from 'react'
import { motion, useScroll, useTransform, useInView } from 'framer-motion'

// Assets
import ahMonogram from './assets/ah-monogram.png'
import palaisExterior from './assets/palais-exterior.jpg'
import founderPortrait from './assets/founder-portrait.jpg'
import palaisCeiling from './assets/palais-ceiling.jpg'
import palaisCeilingWide from './assets/palais-ceiling-wide.jpg'
import perfumeSamples from './assets/perfume-samples.jpg'
import moodAtmosphere from './assets/mood-atmosphere.png'

// Reusable fade-in component
function FadeSection({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-80px' })
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 1, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

const MAILTO = 'mailto:anett@ahbrandhouse.com?subject=Private%20Hospitality%20Dossier%20Request'
const LINKEDIN_URL = 'https://www.linkedin.com/in/anett-horváth-5465342a4?utm_source=share_via&utm_content=profile&utm_medium=member_ios'

export default function App() {
  const { scrollYProgress } = useScroll()
  const [navVisible, setNavVisible] = useState(false)
  const [formOpen, setFormOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => setNavVisible(window.scrollY > 400)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <div className="bg-ivory min-h-screen relative">
      {/* Scroll Progress */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-[2px] bg-gold z-[100] origin-left"
        style={{ scaleX: scrollYProgress }}
      />

      {/* Sticky Navigation */}
      <motion.nav
        initial={{ opacity: 0, y: -20 }}
        animate={navVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
        className="fixed top-0 left-0 right-0 z-50 bg-ivory/90 backdrop-blur-md border-b border-gold/10"
      >
        <div className="max-w-wide mx-auto px-6 md:px-12 flex items-center justify-between h-14">
          <img src={ahMonogram} alt="AH Brandhouse" className="h-8 w-auto max-w-full object-contain shrink-0" />
          <div className="hidden md:flex items-center gap-8 text-xs tracking-[0.15em] uppercase font-sans text-soft-black/70">
            <a href="#scent" className="hover:text-burgundy transition-colors duration-300">Scent</a>
            <a href="#memory" className="hover:text-burgundy transition-colors duration-300">Memory</a>
            <a href="#hospitality" className="hover:text-burgundy transition-colors duration-300">Hospitality</a>
            <a href="#dossier" className="hover:text-burgundy transition-colors duration-300">Dossier</a>
            <a href="#contact" className="hover:text-burgundy transition-colors duration-300">Contact</a>
          </div>
          <a href={MAILTO} className="text-xs tracking-[0.1em] uppercase text-burgundy font-sans hover:text-gold transition-colors">
            Request Dossier
          </a>
        </div>
      </motion.nav>

      {/* ===== HERO SECTION ===== */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={palaisExterior} alt="Palais de la Bourse, Lyon" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-soft-black/70 via-soft-black/50 to-soft-black/80" />
        </div>
        <div className="relative z-10 text-center px-6 md:px-12 max-w-narrow mx-auto">
          <motion.img
            src={ahMonogram}
            alt="AH"
            className="h-16 md:h-20 w-auto max-w-full object-contain mx-auto mb-12 opacity-90"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.9 }}
            transition={{ duration: 2, delay: 0.3 }}
          />
          <motion.h1
            className="font-serif text-hero text-ivory mb-8 text-balance"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.6 }}
          >
            Luxury hospitality is entering a new layer.
          </motion.h1>
          <motion.p
            className="font-sans text-ivory/80 text-subtitle max-w-[600px] mx-auto mb-6 font-light"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 1 }}
          >
            AH Brandhouse designs sensory systems for luxury properties that want to be felt, remembered, and chosen again.
          </motion.p>
          <motion.div
            className="text-ivory/60 font-sans text-sm leading-relaxed max-w-[480px] mx-auto mb-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.4 }}
          >
            <p>Not campaigns. Not generic luxury language. Not surface-level branding.</p>
            <p className="mt-3">A deeper architecture around how a place is perceived, felt, remembered, and returned to.</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.8 }}
          >
            <a
              href={MAILTO}
              className="inline-block px-10 py-4 border border-gold/60 text-gold text-xs tracking-[0.2em] uppercase font-sans hover:bg-gold/10 transition-all duration-500"
            >
              Request Private Dossier
            </a>
            <p className="text-ivory/40 text-xs mt-5 font-sans tracking-wide">
              Private hospitality dossier available upon relevant request.
            </p>
            <p className="text-gold/50 text-xs mt-2 font-sans italic">
              Three strategic hospitality collaborations reviewed for 2026.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== FOUNDER SECTION ===== */}
      <section className="section-padding" id="memory">
        <div className="max-w-content mx-auto">

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <FadeSection delay={0.2}>
              <div className="relative">
                <img
                  src={founderPortrait}
                  alt="Anett Horváth, Founder of AH Brandhouse"
                  className="w-full max-w-[480px] mx-auto object-cover shadow-2xl"
                />
                <div className="absolute -bottom-4 -right-4 w-24 h-24 border border-gold/20" />
              </div>
            </FadeSection>
            <FadeSection delay={0.4}>
              <h2 className="font-serif text-section text-burgundy mb-8">
                Built from instinct.<br />Refined into architecture.
              </h2>
              <div className="space-y-5 text-soft-black/80 font-sans text-[15px] leading-relaxed">
                <p>
                  Anett Horváth's entrepreneurial instinct did not begin with a company. It began in childhood, around the age of eight, when she hand-painted stones and sold them as her first small creations.
                </p>
                <p>
                  That instinct later became Miaamor Rituals, a physical product system built around scent, emotion, and ritual. From there, the work evolved into AH Brandhouse: a sensory brand house focused on memory, perception, and luxury architecture.
                </p>
                <p className="text-soft-black/60 text-sm italic">
                  More about the entrepreneurial story and Miaamor's public validation can be found through RTL's Cápák között, the Hungarian edition of Shark Tank.
                </p>
              </div>
            </FadeSection>
          </div>
        </div>
      </section>

      {/* ===== THE SHIFT SECTION ===== */}
      <section className="section-padding bg-burgundy/[0.03]">
        <div className="max-w-content mx-auto">
          <FadeSection>
            <h2 className="font-serif text-section text-burgundy text-center mb-10">
              The guest has changed.
            </h2>
            <div className="max-w-narrow mx-auto text-center text-soft-black/75 font-sans space-y-4 mb-16">
              <p>
                The premium and luxury audience is shifting. Millennials and Gen Z are already among the most important growth drivers in luxury, and their influence will continue to define which properties become desired, remembered, and chosen.
              </p>
              <p>
                The new luxury guest does not only choose a room, a view, or all-inclusive comfort. They choose a feeling. They choose identity. They choose atmosphere. They choose story. They choose the place that understands them before it speaks to them.
              </p>
              <p>Most luxury hospitality leaders already feel this. Few have built a system around it.</p>
            </div>
          </FadeSection>

          {/* Split comparison */}
          <FadeSection delay={0.3}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 max-w-[700px] mx-auto">
              <div className="p-8 md:p-10 border border-gold/15 md:border-r-0">
                <h3 className="font-serif text-lg text-soft-black/50 mb-6 italic">The old luxury guest</h3>
                <ul className="space-y-3 text-soft-black/60 font-sans text-sm">
                  <li>chooses location</li>
                  <li>compares amenities</li>
                  <li>responds to polish</li>
                  <li>remembers service</li>
                </ul>
              </div>
              <div className="p-8 md:p-10 border border-gold/15 bg-burgundy/[0.02]">
                <h3 className="font-serif text-lg text-burgundy mb-6 italic">The next luxury guest</h3>
                <ul className="space-y-3 text-burgundy/80 font-sans text-sm">
                  <li>chooses emotional atmosphere</li>
                  <li>responds to sensory coherence</li>
                  <li>expects cultural intelligence</li>
                  <li>remembers how the place made them feel</li>
                </ul>
              </div>
            </div>
          </FadeSection>
        </div>
      </section>

      {/* ===== SENSORY SYSTEMS SECTION ===== */}
      <section className="section-padding" id="scent">
        <div className="max-w-content mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <FadeSection>
              <h2 className="font-serif text-section text-burgundy mb-8">
                Sensory systems for luxury hospitality.
              </h2>
              <p className="text-soft-black/70 font-sans mb-10">
                AH Brandhouse works at the intersection of:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { title: 'Luxury hospitality strategy', desc: 'Positioning built for the next decade of luxury travel.' },
                  { title: 'Olfactory neuroscience', desc: 'The science of how scent encodes memory and desire.' },
                  { title: 'Sensory experience design', desc: 'Multi-sensory coherence across every guest touchpoint.' },
                  { title: 'Guest perception', desc: 'How a property is interpreted before, during, and after the stay.' },
                  { title: 'Emotional memory', desc: 'The architecture of what makes a place unforgettable.' },
                  { title: 'Editorial storytelling', desc: 'Narrative systems that feel like private luxury editorials.' },
                  { title: 'Digital prestige', desc: 'Online presence that reflects the same quality as the physical space.' },
                  { title: 'Cultural intelligence', desc: 'Understanding millennial and Gen Z luxury behavior.' },
                  { title: 'Decision-maker positioning', desc: 'Visibility among those who decide, invest, and refer.' },
                ].map((item) => (
                  <div key={item.title} className="group p-4 border border-gold/15 hover:border-gold/40 transition-all duration-500 cursor-default">
                    <p className="font-sans text-sm text-burgundy font-medium">{item.title}</p>
                    <p className="font-sans text-xs text-soft-black/50 mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-500 max-h-0 group-hover:max-h-20 overflow-hidden">{item.desc}</p>
                  </div>
                ))}
              </div>
            </FadeSection>
            <FadeSection delay={0.3}>
              <img
                src={perfumeSamples}
                alt="Olfactory research samples"
                className="w-full object-cover shadow-xl"
              />
              <p className="text-xs text-soft-black/40 font-sans mt-4 italic">
                Scent as material. Memory architecture. Sensory system development.
              </p>
            </FadeSection>
          </div>
        </div>
      </section>

      {/* ===== SERVICES SECTION ===== */}
      <section className="section-padding bg-burgundy/[0.03]" id="hospitality">
        <div className="max-w-content mx-auto">
          <FadeSection>
            <h2 className="font-serif text-section text-burgundy text-center mb-4">
              What can be built.
            </h2>
            <div className="gold-line mx-auto mb-16" />
          </FadeSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Private brand and guest-perception audit', desc: 'A strategic reading of how the property is currently perceived before, during, and after the stay.' },
              { title: 'Millennial and Gen Z luxury perception analysis', desc: 'A cultural and behavioral diagnosis of how the next luxury guest interprets the property.' },
              { title: 'Luxury positioning and cultural relevance diagnosis', desc: 'A refinement layer for properties that want relevance without losing exclusivity.' },
              { title: 'Sensory and olfactory experience recommendations', desc: 'Scent, atmosphere, rhythm, and material cues aligned into one emotional direction.' },
              { title: 'Arrival, suite, spa, wellness, gifting and retention touchpoint strategy', desc: 'The moments where guest memory is formed, strengthened, and recalled.' },
              { title: 'Editorial photography and cinematic content production', desc: 'Visual storytelling designed to feel like a private luxury editorial, not social media volume.' },
              { title: 'High-level LinkedIn and decision-maker storytelling architecture', desc: 'Narrative systems built for the people who decide, invest, book, and refer.' },
              { title: 'Strategic visibility within AH Brandhouse environment', desc: 'Access to a curated context where luxury, fragrance, hospitality, and capital intersect.' },
              { title: 'Optional long-term brand house advisory', desc: 'Ongoing strategic guidance for properties building long-term equity, not short-term campaigns.' },
            ].map((item, i) => (
              <FadeSection key={item.title} delay={i * 0.08}>
                <div className="p-6 md:p-8 border border-gold/15 hover:border-gold/35 transition-all duration-500 h-full bg-ivory/50">
                  <h3 className="font-serif text-lg text-burgundy mb-3 leading-snug">{item.title}</h3>
                  <p className="font-sans text-sm text-soft-black/60 leading-relaxed">{item.desc}</p>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* ===== COLLABORATION MODELS ===== */}
      <section className="section-padding">
        <div className="max-w-content mx-auto">
          <FadeSection>
            <h2 className="font-serif text-section text-burgundy text-center mb-4">
              Three possible levels of collaboration.
            </h2>
            <div className="gold-line mx-auto mb-16" />
          </FadeSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                label: 'Strategic',
                title: 'Strategic Hospitality Engagement',
                desc: 'Paid strategic work focused on perception, positioning, guest memory, sensory recommendations, and content architecture.',
              },
              {
                label: 'Visibility',
                title: 'Win-Win Visibility and Content Collaboration',
                desc: 'A lighter collaboration format focused on editorial content, social media presence, visual assets, cinematic short-form content, and premium audience visibility. Only under specific conditions and always after prior discussion.',
              },
              {
                label: 'Co-Branded',
                title: 'AHBH Collab',
                desc: 'A higher-level, separately priced co-branded activation between AH Brandhouse and the property. Only opened where there is exceptional strategic alignment. May include deeper strategic involvement, co-created storytelling, sensory signature concepts, and public-facing brand association.',
                note: 'Not included by default. Priced separately.',
              },
            ].map((model, i) => (
              <FadeSection key={model.label} delay={i * 0.15}>
                <div className="p-8 md:p-10 border border-gold/20 hover:border-gold/40 transition-all duration-500 h-full relative">
                  <span className="text-xs tracking-[0.2em] uppercase text-gold font-sans">{model.label}</span>
                  <h3 className="font-serif text-xl text-burgundy mt-4 mb-4">{model.title}</h3>
                  <p className="font-sans text-sm text-soft-black/65 leading-relaxed">{model.desc}</p>
                  {model.note && (
                    <p className="font-sans text-xs text-gold/70 mt-4 italic">{model.note}</p>
                  )}
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SELECTION SECTION ===== */}
      <section className="section-padding bg-burgundy/[0.03]">
        <div className="max-w-content mx-auto">
          <FadeSection>
            <h2 className="font-serif text-section text-burgundy text-center mb-6">
              Three properties. One careful selection.
            </h2>
            <p className="text-center text-soft-black/70 font-sans max-w-narrow mx-auto mb-6">
              In 2026, AH Brandhouse is opening only three strategic hospitality collaborations.
            </p>
            <p className="text-center text-soft-black/60 font-sans text-sm max-w-narrow mx-auto mb-12">
              We are especially open to: 5-star all-inclusive luxury properties, wellness-led destinations, hidden retreats, spiritual luxury properties, nature-connected resorts, private estates, boutique sanctuaries, newly launching luxury concepts, and founder-led hospitality destinations.
            </p>
          </FadeSection>
          <FadeSection delay={0.2}>
            <div className="max-w-narrow mx-auto mb-16 p-8 border border-gold/15 bg-ivory/50">
              <p className="font-serif text-lg text-burgundy italic text-center leading-relaxed">
                The most interesting luxury properties are often not the ones everyone already knows. They are the ones standing just before the moment when the right perception system can turn them into an international object of desire.
              </p>
            </div>
          </FadeSection>

          {/* Selection Lens */}
          <FadeSection delay={0.4}>
            <h3 className="font-serif text-xl text-burgundy text-center mb-8">Selection Lens</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-[600px] mx-auto">
              {['Atmosphere', 'Guest Memory Potential', 'Cultural Relevance', 'Sensory Coherence', 'Founder or Property Vision', 'International Desire Potential'].map((criterion, i) => (
                <div key={criterion} className="text-center p-4 border-b border-gold/20">
                  <span className="text-gold text-xs font-sans tracking-wider">{String(i + 1).padStart(2, '0')}</span>
                  <p className="font-sans text-sm text-soft-black/70 mt-1">{criterion}</p>
                </div>
              ))}
            </div>
          </FadeSection>
        </div>
      </section>

      {/* ===== NOT FOR EVERY PROPERTY ===== */}
      <section className="section-padding">
        <div className="max-w-narrow mx-auto text-center">
          <FadeSection>
            <h2 className="font-serif text-section text-burgundy mb-8">
              Not for every property.
            </h2>
            <p className="text-soft-black/70 font-sans mb-4">
              This is not designed for properties seeking volume content, generic influencer visibility, or surface-level luxury language.
            </p>
            <p className="text-soft-black/80 font-sans">
              It is designed for properties with atmosphere, intention, and the ambition to become remembered.
            </p>
          </FadeSection>
        </div>
      </section>

      {/* ===== RECOGNITION SECTION ===== */}
      <section className="section-padding bg-soft-black">
        <div className="max-w-wide mx-auto">
          <FadeSection>
            <h2 className="font-serif text-section text-ivory text-center mb-6">
              Where the work is recognised.
            </h2>
            <p className="text-ivory/60 font-sans text-center max-w-narrow mx-auto mb-16">
              AH Brandhouse does not operate only in theory. Its world has been shaped through real rooms, real audiences, real products, and real decision-making environments.
            </p>
          </FadeSection>

          {/* Editorial image strip */}
          <FadeSection delay={0.2}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="md:col-span-2">
                <img src={palaisCeilingWide} alt="Palais de la Bourse interior" className="w-full h-64 md:h-80 object-cover" />
              </div>
              <div>
                <img src={palaisCeiling} alt="Palais de la Bourse ceiling" className="w-full h-64 md:h-80 object-cover" />
              </div>
            </div>
            <p className="text-ivory/40 text-xs font-sans text-center italic">
              Palais de la Bourse, Lyon. A historical European room of commerce, capital, and cultural exchange.
            </p>
          </FadeSection>

          <FadeSection delay={0.4}>
            <p className="text-ivory/65 font-sans text-center max-w-narrow mx-auto mt-12">
              From product systems and national business validation to European capital environments and international hospitality direction, the work lives where brand, perception, culture, and commerce meet.
            </p>
          </FadeSection>
        </div>
      </section>

      {/* ===== PROOF / CREDIBILITY SECTION ===== */}
      <section className="section-padding">
        <div className="max-w-content mx-auto">
          <FadeSection>
            <h2 className="font-serif text-section text-burgundy text-center mb-6">
              Built organically. From zero.
            </h2>
            <p className="text-soft-black/70 font-sans text-center max-w-narrow mx-auto mb-14">
              Over the past five years, AH Brandhouse has been built organically from zero into a sensory brand house with audience architecture, business validation, luxury and fragrance industry access, and international decision-maker network.
            </p>
          </FadeSection>

          {/* Quiet Proof Notes */}
          <FadeSection delay={0.2}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 max-w-[700px] mx-auto">
              {[
                'Built from zero',
                'Organic audience architecture',
                'National business validation',
                'Luxury and fragrance industry access',
                'International hospitality direction',
                'Founder-led execution',
              ].map((note) => (
                <div key={note} className="text-center py-4 border-b border-gold/15">
                  <p className="font-sans text-sm text-soft-black/65">{note}</p>
                </div>
              ))}
            </div>
          </FadeSection>

          <FadeSection delay={0.4}>
            <div className="mt-14 text-center">
              <p className="font-sans text-xs text-soft-black/50 tracking-wide">
                Miaamor Rituals · Maison Aurelia · AH Brandhouse · RTL Cápák között
              </p>
            </div>
          </FadeSection>
        </div>
      </section>

      {/* ===== MOOD ATMOSPHERE ===== */}
      <section className="relative h-[40vh] md:h-[50vh] overflow-hidden">
        <img src={moodAtmosphere} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-ivory/30 to-ivory/60" />
      </section>

      {/* ===== PRIVATE DOSSIER SECTION ===== */}
      <section className="section-padding" id="dossier">
        <div className="max-w-narrow mx-auto text-center">
          <FadeSection>
            <h2 className="font-serif text-section text-burgundy mb-8">
              The full dossier is private.
            </h2>
            <p className="text-soft-black/70 font-sans mb-4">
              The detailed portfolio, private hospitality dossier, selected case materials, and extended strategic documents are not published openly.
            </p>
            <p className="text-soft-black/60 font-sans mb-12">
              They are shared only upon relevant request, after understanding the property, its current positioning, and the potential alignment.
            </p>
          </FadeSection>

          {/* Gated card effect */}
          <FadeSection delay={0.2}>
            <div className="relative p-10 md:p-14 border border-gold/25 bg-ivory-warm/50 mx-auto max-w-[400px] mb-10 overflow-hidden">
              <div className="absolute inset-0 backdrop-blur-[2px]" />
              <div className="relative z-10">
                <img src={ahMonogram} alt="AH" className="h-14 w-auto max-w-full object-contain mx-auto mb-6 opacity-70" />
                <p className="font-serif text-lg text-burgundy/70 italic">Private Hospitality Dossier</p>
                <div className="mt-6 space-y-1.5 text-xs text-soft-black/40 font-sans">
                  <p>Available upon relevant request.</p>
                  <p>Reviewed privately.</p>
                  <p>Shared with discretion.</p>
                </div>
              </div>
            </div>
          </FadeSection>

          <FadeSection delay={0.4}>
            <a
              href={MAILTO}
              className="inline-block px-10 py-4 bg-burgundy text-ivory text-xs tracking-[0.2em] uppercase font-sans hover:bg-burgundy-light transition-all duration-500"
            >
              Request Private Dossier
            </a>
            <p className="text-soft-black/40 text-xs mt-4 font-sans">
              For relevant, pre-qualified inquiries only.
            </p>
          </FadeSection>
        </div>
      </section>

      {/* ===== REFERRALS ===== */}
      <section className="px-6 md:px-12 pb-20">
        <div className="max-w-narrow mx-auto text-center border-t border-gold/10 pt-16">
          <FadeSection>
            <h3 className="font-serif text-xl text-burgundy mb-4">Referrals are welcome.</h3>
            <p className="text-soft-black/60 font-sans text-sm max-w-[500px] mx-auto">
              We are especially open to introductions to hidden, spiritual, nature-connected, wellness-led, or newly launching luxury properties with the potential to become internationally desired destination brands.
            </p>
            <p className="text-soft-black/40 font-sans text-xs mt-4 italic">
              All referrals are reviewed privately.
            </p>
          </FadeSection>
        </div>
      </section>

      {/* ===== DOSSIER REQUEST FORM ===== */}
      <section className="section-padding bg-burgundy/[0.03]">
        <div className="max-w-[550px] mx-auto">
          <FadeSection>
            <h2 className="font-serif text-section text-burgundy text-center mb-4">
              Request Private Review
            </h2>
            <p className="text-soft-black/60 font-sans text-center text-sm mb-10">
              For selected properties, partners, or relevant referrals.
            </p>
            <form
              className="space-y-5"
              onSubmit={(e) => {
                e.preventDefault()
                window.location.href = MAILTO
              }}
            >
              {[
                { name: 'name', label: 'Name', type: 'text' },
                { name: 'property', label: 'Property / Company', type: 'text' },
                { name: 'role', label: 'Role', type: 'text' },
                { name: 'email', label: 'Email', type: 'email' },
                { name: 'linkedin', label: 'LinkedIn profile', type: 'url' },
                { name: 'website', label: 'Property website', type: 'url' },
              ].map((field) => (
                <div key={field.name}>
                  <label className="block text-xs font-sans text-soft-black/50 mb-1.5 tracking-wide uppercase">{field.label}</label>
                  <input
                    type={field.type}
                    name={field.name}
                    className="w-full bg-transparent border-b border-gold/25 focus:border-gold/60 outline-none py-2.5 font-sans text-sm text-soft-black transition-colors"
                  />
                </div>
              ))}
              <div>
                <label className="block text-xs font-sans text-soft-black/50 mb-1.5 tracking-wide uppercase">Type of inquiry</label>
                <select className="w-full bg-transparent border-b border-gold/25 focus:border-gold/60 outline-none py-2.5 font-sans text-sm text-soft-black/70 transition-colors">
                  <option value="">Select...</option>
                  <option value="strategic">Strategic Hospitality Engagement</option>
                  <option value="visibility">Win-Win Visibility Collaboration</option>
                  <option value="collab">AHBH Collab</option>
                  <option value="referral">Referral</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-sans text-soft-black/50 mb-1.5 tracking-wide uppercase">Message</label>
                <textarea
                  name="message"
                  rows={3}
                  className="w-full bg-transparent border-b border-gold/25 focus:border-gold/60 outline-none py-2.5 font-sans text-sm text-soft-black resize-none transition-colors"
                />
              </div>
              <div className="pt-4">
                <button
                  type="submit"
                  className="w-full py-4 bg-burgundy text-ivory text-xs tracking-[0.2em] uppercase font-sans hover:bg-burgundy-light transition-all duration-500"
                >
                  Request Private Review
                </button>
                <p className="text-xs text-soft-black/35 font-sans mt-3 text-center">
                  Private review only. Shared upon relevant request.
                </p>
              </div>
            </form>
          </FadeSection>
        </div>
      </section>

      {/* ===== CONTACT SECTION ===== */}
      <section className="section-padding bg-soft-black" id="contact">
        <div className="max-w-narrow mx-auto text-center">
          <FadeSection>
            <h2 className="font-serif text-section text-ivory mb-6">
              Begin with discretion.
            </h2>
            <p className="text-ivory/60 font-sans mb-10">
              For selected properties, partners, or relevant referrals, please reach out via LinkedIn or email.
            </p>
            <a
              href={MAILTO}
              className="inline-block text-gold font-sans text-sm tracking-wide hover:text-gold-light transition-colors"
            >
              anett@ahbrandhouse.com
            </a>
            <p className="text-ivory/30 text-xs font-sans mt-6 max-w-[400px] mx-auto italic">
              Please treat this email address as confidential. Do not add it to public lists, databases, group communication, or third-party outreach without prior permission.
            </p>
          </FadeSection>

          <FadeSection delay={0.3}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
              <a
                href={MAILTO}
                className="px-10 py-4 bg-gold/10 border border-gold/40 text-gold text-xs tracking-[0.2em] uppercase font-sans hover:bg-gold/20 transition-all duration-500"
              >
                Request Private Dossier
              </a>
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="px-10 py-4 border border-ivory/20 text-ivory/70 text-xs tracking-[0.2em] uppercase font-sans hover:border-ivory/40 hover:text-ivory transition-all duration-500"
              >
                Contact via LinkedIn
              </a>
            </div>
            <p className="text-ivory/25 text-xs font-sans mt-6">
              Three collaborations reviewed for 2026. No public portfolio access.
            </p>
          </FadeSection>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="px-6 md:px-12 py-12 bg-soft-black border-t border-gold/10">
        <div className="max-w-content mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img src={ahMonogram} alt="AH Brandhouse" className="h-8 w-auto max-w-full object-contain shrink-0 opacity-70" />
            <div className="text-ivory/40 font-sans text-xs">
              <p className="tracking-wider">AH Brandhouse</p>
              <p className="text-ivory/25 mt-0.5">Maison Aurelia · Miaamor Rituals</p>
            </div>
          </div>
          <div className="text-center md:text-right text-ivory/30 font-sans text-xs">
            <p>Sensory Systems for Luxury Hospitality</p>
            <p className="mt-1">Budapest / International</p>
          </div>
        </div>
      </footer>

      {/* ===== STICKY MOBILE CTA ===== */}
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-burgundy/95 backdrop-blur-sm border-t border-gold/20 safe-area-bottom">
        <a
          href={MAILTO}
          className="block text-center py-4 text-ivory text-xs tracking-[0.2em] uppercase font-sans"
        >
          Request Private Dossier
        </a>
      </div>
    </div>
  )
}
